import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../utils/userAuth';
import { User } from '../types/Patient';

interface InquiryAuthContextType {
    inquiryData: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
    // user: User | null;
}

const InquiryAuthContext = createContext<InquiryAuthContextType | undefined>(undefined);

export const useInquiryAuth = () => {
    const context = useContext(InquiryAuthContext);
    if (!context) {
        throw new Error('useInquiryAuth must be used within an InquiryAuthProvider');
    }
    return context;
};

interface InquiryAuthProviderProps {
    children: ReactNode;
}

export const InquiryAuthProvider: React.FC<InquiryAuthProviderProps> = ({ children }) => {
    const [inquiryData, setInquiryData] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const handleForcedLogout = useCallback(() => {
        clearUserSession();
        setToken(null);
        setInquiryData(null);
        navigate('/user/login');
    }, [navigate]);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('userToken');
            const storedInquiry = localStorage.getItem('inquiryInfo');

            if (storedToken) {
                setToken(storedToken);
            }

            if (storedInquiry) {
                try {
                    const inquiryDataParsed = JSON.parse(storedInquiry);
                    setInquiryData(inquiryDataParsed);
                } catch (error) {
                    clearUserSession();
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, []);

    const logout = useCallback(() => {
        handleForcedLogout();
    }, [handleForcedLogout]);

    const isAuthenticated = !!token;

    return (
        <InquiryAuthContext.Provider
            value={{
                inquiryData,
                token,
                isAuthenticated,
                isLoading,
                logout,
                // user: inquiryData,
            }}
        >
            {children}
        </InquiryAuthContext.Provider>
    );
};
